import { PageContext } from 'context/PageContext';
import { useContext, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { addFormItem, resetFormItem } from 'store/slices/calculatorFormSlice';
import { isScalar } from 'utilities';
import { computeBirthdateFromAge } from '../utilities/format';

/**
 * Registry of CMS `DataLoadEventBlock.EventToLoad` handlers. Each maps the
 * raw source value (selected from Redux by the hook) to the string that
 * should be written into any `HiddenFieldBlock` whose
 * `dataEventConfiguration.eventToLoad` matches. Return `undefined` to skip.
 * Mirrors `DataLoadEventSelectionFactory` in abg-cms.
 */
const EVENT_HANDLERS: Record<string, (source: unknown) => string | undefined> = {
	LoadCustomerNumber: (id) => (isScalar(id) ? String(id) : undefined),
	LoadCustomerPostalCode: (zip) => (isScalar(zip) ? String(zip) : undefined),
	LoadCustomerBirthDate: (age) => {
		const n = Number(age);
		return Number.isFinite(n) && n > 0 ? computeBirthdateFromAge(n) : undefined;
	},
};

/**
 * Populates `HiddenFieldBlock`s declared with a
 * `DataEventConfiguration.EventToLoad` in CMS `metaArea.staticFields` by
 * projecting the corresponding Redux value through {@link EVENT_HANDLERS}.
 * Runs whenever any watched source changes so calculation payloads stay
 * up to date without a step submit.
 */
export const useDataLoadEvents = () => {
	const pageContext = useContext(PageContext);
	const dispatch = useAppDispatch();
	const staticFieldItems = pageContext?.metaArea?.items?.[0]?.content?.staticFields?.items;

	const customerId = useAppSelector((state) => state.customer?.customer_id);
	const postalCode = useAppSelector((state) => state.customer?.contact?.address?.postal_code);
	const customerAge = useAppSelector((state) => state.calculatorForm?.CUSTOMER_AGE?.data?.value);

	useEffect(() => {
		if (!staticFieldItems?.length) return;

		const sources: Record<string, unknown> = {
			LoadCustomerNumber: customerId,
			LoadCustomerPostalCode: postalCode,
			LoadCustomerBirthDate: customerAge,
		};

		for (const item of staticFieldItems) {
			const content = item.content as any;
			if (content?.contentType !== 'HiddenFieldBlock') continue;
			const eventName: string | undefined = content.dataEventConfiguration?.eventToLoad;
			if (!eventName) continue;
			const handler = EVENT_HANDLERS[eventName];
			if (!handler) continue;
			const key = content.uniqueIdentifier?.uid;
			if (!key) continue;
			const value = handler(sources[eventName]);
			// Clear the stored value when the source is missing so calculation doesn't reuse a stale birthdate etc.
			if (value == null) {
				dispatch(resetFormItem({ key }));
				continue;
			}
			dispatch(
				addFormItem({
					key,
					item: {
						data: { id: key, value, type: 'HiddenFieldBlock' },
						page: '0',
					},
				})
			);
		}
	}, [customerId, postalCode, customerAge, staticFieldItems, dispatch]);
};
